<script lang="ts">
// Deliberately no background: an opaque table paints square corners over a rounded
// container. Cells that must stay opaque while content scrolls under them (pinned
// columns, the floating header clone) set their own. Anything that does paint inside
// (hover tints, pinned cells) is clipped by the scroll container, which inherits the
// parent's radius when it is the parent's last child — i.e. when it sits on the corners.
export const tableClass =
  'w-full min-w-full caption-bottom border-collapse text-xs text-primal [&_tr]:border-b [&_tr]:border-default [&_tbody_tr:last-child]:border-b-0'
</script>

<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { useResizeObserver, useScroll } from '@vueuse/core'
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import { cn } from '#ui-kit/lib/utils'

const props = defineProps<{
  class?: HTMLAttributes['class']
}>()

const container = useTemplateRef<HTMLDivElement>('container')
const tableEl = useTemplateRef<HTMLTableElement>('tableEl')

// Drives the pinned-column dividers: a left one only shows once content has
// scrolled under the pinned cells, a right one only while content still hides
// under them. See `table-pinned-left` / `table-pinned-right` in styles/utilities.css.
const { x } = useScroll(container)
const scrolledX = computed(() => x.value > 0)

const scrollXEnd = ref(true)

function measureScrollXEnd() {
  const el = container.value
  scrollXEnd.value = !el || el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
}

watch(x, measureScrollXEnd)
useResizeObserver(container, measureScrollXEnd)
useResizeObserver(tableEl, measureScrollXEnd)
onMounted(measureScrollXEnd)

defineExpose({
  getContainer: () => container.value,
})
</script>

<template>
  <div
    ref="container"
    data-slot="table-container"
    :data-scrolled-x="scrolledX || undefined"
    :data-scroll-x-end="scrollXEnd || undefined"
    class="relative w-full overflow-x-auto contain-inline-size last:rounded-[inherit]"
  >
    <table ref="tableEl" data-slot="table" :class="cn(tableClass, props.class)">
      <slot />
    </table>
  </div>
</template>
